import { ChartConfig, ChartContainer } from '@/design-system/chart/chart';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { Currency } from '@seapoint/server/enums';
import { ComponentProps, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, XAxis } from 'recharts';
import { CategoricalChartState } from 'recharts/types/chart/types';
import { ReturnData } from '../../utils/get-return-data';
import { EmptyStateCard } from './empty-state-card';
import { usePeriodSelection } from './period-selection-context/use-period-selection';

const SUCCESS_COLOR = 'var(--color-success)';
const SUCCESS_COLOR_FADED = 'oklch(from var(--color-success) l c h / 0.4)';
const DESTRUCTIVE_COLOR = 'var(--color-destructive)';
const DESTRUCTIVE_COLOR_FADED = 'oklch(from var(--color-destructive) l c h / 0.4)';

const chartConfig = {
  totalReturn: {
    label: 'Returns',
    color: 'var(--color-success)',
  },
} satisfies ChartConfig;

type ReturnsChartConfig =
  | { variant: 'EMPTY_STATE_NO_KIID'; monthlyReturns: ReturnData['monthlyReturns'] }
  | {
      variant: 'MONTH';
      monthlyReturns: ReturnData['monthlyReturns'];
      currency: Currency;
    }
  | {
      variant: 'YEAR';
      yearlyReturns: ReturnData['yearlyReturns'];
      currency: Currency;
    };

type ReturnsChartProps = {
  returnsChartConfig: ReturnsChartConfig;
};

export const ReturnsChart = ({ returnsChartConfig }: ReturnsChartProps) => {
  const { onSelectPeriod, resetToFirstPeriod } = usePeriodSelection();
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const { data, label } = getChartData(returnsChartConfig);

  const shouldDisplayEmptyState = returnsChartConfig.variant === 'EMPTY_STATE_NO_KIID';

  const handleMouseMove = (state: CategoricalChartState) => {
    const newIndex = state.activeTooltipIndex ?? null;

    if (state.activeTooltipIndex === activeIndex) {
      return;
    }

    setActiveIndex(newIndex);

    if (newIndex !== null && data[newIndex].totalReturn !== 0) {
      onSelectPeriod(data[newIndex].period);
    }
  };

  const handleMouseLeave = () => {
    setActiveIndex(0);
    resetToFirstPeriod();
  };

  return (
    <div className="relative w-full">
      <ChartContainer config={chartConfig} className="h-[214px] w-full">
        <BarChart accessibilityLayer data={data} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
          <CartesianGrid vertical={false} />

          {!shouldDisplayEmptyState && (
            <XAxis
              dataKey={label}
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              reversed
              ticks={
                data.filter((d) => d.totalReturn !== 0).map((d) => ('label' in d ? d.label : d.period)) as string[]
              }
              tickFormatter={(value) => {
                // For MONTH variant: shorten "November" -> "Nov"
                // For YEAR variant: keep "2025" as-is
                if (returnsChartConfig.variant === 'MONTH') {
                  return value.substring(0, 3);
                }

                return value;
              }}
            />
          )}

          <Bar dataKey="totalReturn" fill="var(--color-success)" radius={8} activeBar={{ fill: 'yellow' }}>
            {data.map((item, index) => (
              <Cell key={item.period} fill={getCellFillColor(item, activeIndex === index)} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>

      {shouldDisplayEmptyState && <EmptyStateCard />}
    </div>
  );
};

type GetChartDataReturnType = { data: ReturnData['monthlyReturns'] | ReturnData['yearlyReturns']; label: string };

const getChartData = (config: ComponentProps<typeof ReturnsChart>['returnsChartConfig']): GetChartDataReturnType => {
  const { variant } = config;

  switch (variant) {
    case 'EMPTY_STATE_NO_KIID':
    case 'MONTH':
      return { data: config.monthlyReturns, label: 'label' };
    case 'YEAR':
      return { data: config.yearlyReturns, label: 'period' };
    default:
      return exhaustiveCheck(variant);
  }
};

const getCellFillColor = (
  item: ReturnData['monthlyReturns'][number] | ReturnData['yearlyReturns'][number],
  isActive: boolean,
) => {
  const successColor = isActive ? SUCCESS_COLOR : SUCCESS_COLOR_FADED;
  const destructiveColor = isActive ? DESTRUCTIVE_COLOR : DESTRUCTIVE_COLOR_FADED;

  return item.totalReturn > 0 ? successColor : destructiveColor;
};
